// TibberPrice/TibberPriceEngine.js
// Engine Pattern — extends EngineCleanupBase, singleton export.
// A getPrice() miss triggers a throttled background fetch of that UTC day, so callers stay synchronous.
import EngineCleanupBase from '../../../services/engines/EngineCleanupBase';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import df from '../../../services/utils/DateFormat';
import tibberPriceCache from './TibberPriceCache';
import tibberPriceFetcher from './TibberPriceFetcher';
import { TIBBER_TYPE, TIBBER_STEP_MS, EXPORT_FEE, TIBBER_RETRY_MS } from './tibberConst';

const _name = 'TibberPriceEngine';

class TibberPriceEngine extends EngineCleanupBase {
    _lastTry = new Map(); // `${type}_${dayEms}` -> ms of the last fetch attempt
    _failed = new Set(); // `${type}_${dayEms}` of days whose last fetch failed

    bootup = () => {
        this.initialize(_name);
    };

    /**
     * Fetches the prices of the UTC day that contains `ems` (up to the last published slot).
     * @param {string} [type] - 'quarter' or 'hour'
     * @param {number} [ems] - any time within the wanted day, default now
     * @returns {Promise<void>}
     */
    fetch = async (type = TIBBER_TYPE.QUARTER, ems = Date.now()) => {
        try {
            const dayEms = df.startOfDate(ems);
            const key = `${type}_${dayEms}`;
            this._lastTry.set(key, Date.now());

            const items = await tibberPriceFetcher.fetch(type, dayEms);
            const errorChanged = this._setFailed(key, items === null);
            if (items?.length) tibberPriceCache.setPrices(type, items);
            if (errorChanged || items?.length) this._notify(type);
        } catch (error) {
            this.logError('fetch', error.message);
        }
    };

    /**
     * @param {number} [ems] - any time within the wanted slot, default now
     * @param {string} [type] - 'quarter' or 'hour'
     * @returns {{ems:number,total:number,energy:number,tax:number,level:string}|null}
     */
    getPrice = (ems = Date.now(), type = TIBBER_TYPE.QUARTER) => {
        const step = TIBBER_STEP_MS[type];
        const price = tibberPriceCache.getPrice(type, Math.floor(ems / step) * step);
        if (!price) this._requestDay(type, ems);

        return price;
    };

    getCurrentPrice = (type = TIBBER_TYPE.QUARTER) => this.getPrice(Date.now(), type);

    /**
     * @returns {number|null} Total minus the selling fee, per kWh
     */
    getExportPrice = (ems = Date.now(), type = TIBBER_TYPE.QUARTER) => {
        const price = this.getPrice(ems, type);
        return price ? price.total - EXPORT_FEE : null;
    };

    /**
     * @param {string} [type]
     * @returns {object[]} cached slots, oldest first
     */
    getAll = (type = TIBBER_TYPE.QUARTER) => tibberPriceCache.getAll(type);

    /**
     * Makes sure today and tomorrow (UTC days) are cached for this type. Days that are already
     * cached are skipped, missing days are retried at most once per TIBBER_RETRY_MS.
     * @param {string} [type]
     */
    ensureDays = (type = TIBBER_TYPE.QUARTER) => {
        [0, 1].forEach((offset) => {
            const ems = Date.now() + offset * df.MillisecondsInDay;
            if (!tibberPriceCache.hasDay(type, df.startOfDate(ems))) this._requestDay(type, ems);
        });
    };

    /**
     * Force-refetches today and tomorrow (UTC days), bypassing the cache and retry throttle.
     * @param {string} [type]
     * @returns {Promise<void>}
     */
    reload = async (type = TIBBER_TYPE.QUARTER) => {
        await Promise.all([0, 1].map((offset) => this.fetch(type, Date.now() + offset * df.MillisecondsInDay)));
    };

    /**
     * @param {string} [type]
     * @param {number} [offset] - UTC days from today
     * @returns {boolean} true when the last fetch of that day failed
     */
    hasError = (type = TIBBER_TYPE.QUARTER, offset = 0) =>
        this._failed.has(`${type}_${df.startOfDate(Date.now() + offset * df.MillisecondsInDay)}`);

    // returns true when the failed state of this key changed
    _setFailed = (key, failed) => {
        if (failed === this._failed.has(key)) return false;
        if (failed) this._failed.add(key);
        else this._failed.delete(key);
        return true;
    };

    _requestDay = (type, ems) => {
        const key = `${type}_${df.startOfDate(ems)}`;
        if (Date.now() - (this._lastTry.get(key) ?? 0) < TIBBER_RETRY_MS) return;

        this.fetch(type, ems);
    };

    _notify = (type) => {
        ComponentNotifier.notify(_name, 'dataChanged', { type });
    };

    clear = () => {
        tibberPriceCache.clear();
        this._lastTry.clear();
        this._failed.clear();
        super.clear();
    };
}

const tibberPriceEngine = new TibberPriceEngine();
export default tibberPriceEngine;
